import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { HeaderFilterControl, HeaderFilterGroup } from 'shared/components/filter-header'
import useMediaQuery from 'shared/hooks/use-media-query'
import { deepCompare } from 'shared/utils/deepCompare'

/**
 * Filter values are compared by their JSON form — the form they reach the API and the URL in. So
 * `undefined` equals a missing key, a `Date` equals another `Date` with the same ISO string, and
 * `Map`, `Set` and class instances must never appear in a filter value: having no JSON form, they
 * would all compare equal to each other.
 */
export const filterValuesEqual = deepCompare

interface FilterDraftOptions<T> {
  applied: T
  defaults: T
  onApply: (values: T) => void
  /**
   * The keys whose control is unavailable for the current values — hidden by a business condition
   * (Module before a Course is picked) or by a permission. They are forced back to `defaults`, so
   * an unavailable field cannot filter the data invisibly. Returning keys rather than a rewritten
   * object is what keeps the rule convergent: the hook only ever resets, so it cannot loop.
   */
  unavailableFields?: (values: T) => readonly (keyof T)[]
  /**
   * Whether the group's field metadata has loaded, so the values it would send mean something.
   * A pending draft waits rather than being dropped. Not value validation: §5.1 leaves an
   * incomplete date to the control, which hands the group a usable value.
   */
  ready?: boolean
}

export interface FilterDraftState<T> {
  draft: T
  applied: T
  defaults: T
  setDraft: (update: React.SetStateAction<T>) => void
  setInline: (inline: boolean) => void
  apply: () => boolean
  clear: () => void
  discard: () => void
  dirty: boolean
  canApply: boolean
}

export function useFilterDraft<T>(options: FilterDraftOptions<T>): FilterDraftState<T> {
  const { isMobile } = useMediaQuery()
  const [inline, setInline] = useState(false)
  const autoApply = !isMobile || inline
  const [draft, setDraftState] = useState(options.applied)
  const latest = useRef({ ...options, autoApply })
  const values = useRef(draft)
  const applied = useRef(options.applied)
  const external = useRef(options.applied)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const cancel = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = undefined
  }, [])

  // A predicate reads the other values, so a chain (Course → Module → Lecture) needs more than one
  // pass. Each pass moves at least one field to its default and never away from it, which bounds
  // the loop by the number of keys.
  const settle = useCallback((values: T) => {
    const { defaults, unavailableFields } = latest.current
    if (!unavailableFields) return values
    let next = values
    for (let pass = Object.keys(defaults as object).length; pass >= 0; pass--) {
      const stale = unavailableFields(next).filter(
        key => !filterValuesEqual(next[key], defaults[key]),
      )
      if (stale.length === 0) break
      next = { ...next }
      for (const key of stale) next[key] = defaults[key]
    }
    return next
  }, [])

  const apply = useCallback(() => {
    cancel()
    const next = settle(values.current)
    if (latest.current.ready === false) return false
    values.current = next
    setDraftState(next)
    if (filterValuesEqual(next, applied.current)) return true
    applied.current = next
    latest.current.onApply(next)
    return true
  }, [cancel, settle])

  const setDraft = useCallback(
    (update: React.SetStateAction<T>) => {
      const next =
        typeof update === 'function' ? (update as (previous: T) => T)(values.current) : update
      const settled = settle(next)
      if (filterValuesEqual(settled, values.current)) return
      values.current = settled
      setDraftState(settled)
      cancel()
      if (latest.current.autoApply && !filterValuesEqual(settled, applied.current))
        timer.current = setTimeout(apply, 500)
    },
    [apply, cancel, settle],
  )

  const clear = useCallback(() => {
    cancel()
    values.current = latest.current.defaults
    setDraftState(values.current)
    apply()
  }, [apply, cancel])

  const discard = useCallback(() => {
    cancel()
    values.current = applied.current
    setDraftState(applied.current)
  }, [cancel])

  // Runs on every render: it mirrors the owner's options into `latest`, notices an externally
  // restored `applied`, and re-arms the timer after a change of placement, readiness or field
  // availability. It never starts a second timer, so an equal rerender cannot extend a pending one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    latest.current = { ...options, autoApply }
    if (!autoApply) cancel()
    if (!filterValuesEqual(external.current, options.applied)) {
      external.current = options.applied
      // An acknowledgement must not erase edits made while the owner was rendering.
      if (!filterValuesEqual(applied.current, options.applied)) {
        cancel()
        applied.current = options.applied
        values.current = options.applied
        setDraftState(options.applied)
      }
    }
    // Availability also constrains Applied, independently of pending mobile edits.
    const availableApplied = settle(applied.current)
    if (options.ready !== false && !filterValuesEqual(availableApplied, applied.current)) {
      applied.current = availableApplied
      options.onApply(availableApplied)
    }
    const settled = settle(values.current)
    if (!filterValuesEqual(settled, values.current)) {
      // Written straight through rather than through `setDraft`: a reset the user did not type
      // must not cancel a live timer and start a fresh 500ms window.
      values.current = settled
      setDraftState(settled)
    }
    if (
      autoApply &&
      timer.current === undefined &&
      !filterValuesEqual(values.current, applied.current) &&
      options.ready !== false
    )
      timer.current = setTimeout(apply, 500)
  })
  useEffect(() => cancel, [cancel])

  const dirty = !filterValuesEqual(draft, options.defaults)
  return {
    draft,
    applied: options.applied,
    defaults: options.defaults,
    setDraft,
    setInline,
    apply,
    clear,
    discard,
    dirty,
    canApply: dirty && options.ready !== false,
  }
}

export interface FilterControl<T> extends HeaderFilterControl {
  /**
   * The keys this one control drives as a *single* logical filter — a range picker owning
   * `dateSince` and `dateTill`. Every other control leaves this out: each key of `defaults`
   * already counts as one filter.
   */
  fields?: readonly (keyof T)[]
}

interface FilterGroupOptions {
  complex?: boolean
}

/**
 * Builds the header's group contract. Only contract fields reach the header: `draft` and
 * `setDraft` stay with the module that renders the controls.
 */
export function toFilterGroup<T>(
  state: FilterDraftState<T>,
  controls: readonly FilterControl<T>[],
  options: FilterGroupOptions = {},
): HeaderFilterGroup {
  return {
    controls: controls.map(({ key, node }) => ({ key, node })),
    activeCount: countActiveFilters(
      state.applied,
      state.defaults,
      controls.flatMap(control => (control.fields ? [control.fields] : [])),
    ),
    dirty: state.dirty,
    canApply: state.canApply,
    clear: state.clear,
    apply: state.apply,
    setInline: state.setInline,
    discard: state.discard,
    complex: options.complex,
  }
}

/**
 * Every key of `defaults` is one logical filter, which is why a group's `defaults` must hold
 * exactly the values that group owns and nothing else. `composites` merge keys driven by one
 * control into a single filter.
 */
function countActiveFilters<T>(
  applied: T,
  defaults: T,
  composites: readonly (readonly (keyof T)[])[],
) {
  const merged = new Set<keyof T>(composites.flat())
  const logical: readonly (keyof T)[][] = [
    ...composites.map(keys => [...keys]),
    ...(Object.keys(defaults as object) as (keyof T)[])
      .filter(key => !merged.has(key))
      .map(key => [key]),
  ]
  return logical.filter(keys => keys.some(key => !filterValuesEqual(applied[key], defaults[key])))
    .length
}
